/* eslint-disable max-lines */
import { useEffect, useState } from 'react';
import { useCTA } from '../../../hooks/useCTA';
import { useSearch } from '../../../hooks/useSearch';
import { useStatefulScrollLock } from '../../../hooks/useStatefulScrollLock';
import { useWhiteLabelCustomisations } from '../../../hooks/useWhiteLabelCustomisations';
import { useMobile } from '../../../services/screenSize/ScreenSizeService';
import { DesktopNavMenu } from '../DesktopNavMenu/DesktopNavMenu';
import { NewHeaderContainer } from '../HeaderContainer/HeaderContainer';
import { MobileMenu } from '../MobileMenu/MobileMenu';
import { NewSearchBar } from '../NewSearchBar/NewSearchBar';
import { OverlayMask } from '../OverlayMask/OverlayMask';
import { CompactSearchIcon, CompactSearchIconBackground } from '../SearchBar/SearchBar.styles';
import {
  NewBackArrowIcon,
  NewHamburgerContainer,
  StyledClickableLogo,
  StyledCTADot,
  NewStyledHamburgerIcon,
  SearchBarCloseIcon,
  SearchBarInstructionText,
} from '../SearchHeader.styles';
import { SlideOut } from '../SlideOut/SlideOut';
import { SearchPromotionItem } from '../Suggestions/EmptySearchSuggestions/SearchPromotionResponse.type';
import { Transition } from '../Transition/Transition';
import { FilterChoice } from '../types/FilterChoice';

interface Props {
  placeholder?: string;
  disableSearch?: boolean;
  showLoginDropdown?: boolean;
  compactSearch?: boolean;
  searchSpecialty?: SearchPromotionItem | null;
}

export const SearchHeaderV2 = ({
  placeholder,
  disableSearch,
  compactSearch,
  showLoginDropdown,
}: Props) => {
  const showExternalActions = useWhiteLabelCustomisations().showExternalActions;
  const [isSearchMultiLine, setIsSearchMultiLine] = useState(false);
  const [specialtyFocused, setSpecialtyFocused] = useState<boolean>(false);
  const [locationFocused, setLocationFocused] = useState<boolean>(false);
  const [expanded, setExpanded] = useState(false);
  const { setLocked: setSearchScrollLock } = useStatefulScrollLock();
  const [showingMobileMenu, setShowingMobileMenu] = useState<boolean>(false);

  const [searchResultsSingleViewIsShowing, setSearchResultsSingleViewIsShowing] = useState<boolean>(
    false,
  );

  const { focusMobileSearch, setFocusMobileSearch } = useSearch();

  useEffect(() => {
    if (focusMobileSearch) {
      setIsSearchMultiLine(true);
      setShowPlaceholder(false);
      setLocationFocused(false);
      setSpecialtyFocused(true);
      setSearchResultsSingleViewIsShowing(false);
      setFocusMobileSearch(false);
    }
  }, [focusMobileSearch, setFocusMobileSearch]);

  const mobile = useMobile();
  const desktop = !mobile;

  const handleOverlayClick = () => {
    setSearchScrollLock(false);
    setLocationFocused(false);
    setSpecialtyFocused(false);
    setExpanded(false);
    setIsSearchMultiLine(false);
  };

  const openMenu = () => {
    setSearchScrollLock(true);
    setShowingMobileMenu(true);
  };

  const closeMenu = () => {
    setSearchScrollLock(false);
    setShowingMobileMenu(false);
  };

  const onCompactToggle = (val: boolean) => {
    setSpecialtyFocused(val);
    setExpanded(val);
  };

  const [showPlaceholder, setShowPlaceholder] = useState<boolean>(true);

  const backButtonClickHandler = () => {
    if (searchResultsSingleViewIsShowing) {
      setSearchResultsSingleViewIsShowing(false);
      return;
    }

    setSpecialtyFocused(false);
    setLocationFocused(false);
    setShowPlaceholder(true);
  };

  const willShowSearchBar =
    (!disableSearch && !compactSearch) || (compactSearch && expanded && showExternalActions);

  const { cta } = useCTA();

  const showAvatarCTA = cta?.showMctCTA || cta?.showHealthInterestsCTA;

  return (
    <>
      {desktop && (
        <Transition showing={specialtyFocused || locationFocused}>
          <OverlayMask onClickOverlay={handleOverlayClick} />
        </Transition>
      )}

      {mobile && isSearchMultiLine && <OverlayMask onClickOverlay={handleOverlayClick} />}

      <NewHeaderContainer
        focused={specialtyFocused || locationFocused}
        isSearchMultiLine={isSearchMultiLine}
      >
        {desktop && (
          <StyledClickableLogo supportWhiteLabel textLogo={compactSearch || disableSearch} />
        )}

        {mobile && (
          <>
            {!specialtyFocused && !locationFocused && !isSearchMultiLine && (
              <StyledClickableLogo supportWhiteLabel textLogo={compactSearch || disableSearch} />
            )}
            {(specialtyFocused || locationFocused) && (
              <NewBackArrowIcon onClick={backButtonClickHandler} />
            )}
            {isSearchMultiLine && !specialtyFocused && !locationFocused && (
              <>
                <SearchBarCloseIcon onClick={() => setIsSearchMultiLine(false)} colour="black" />
                <SearchBarInstructionText>Edit service or location</SearchBarInstructionText>
              </>
            )}
          </>
        )}

        {willShowSearchBar && (
          <NewSearchBar
            placeholder={placeholder}
            specialtyFocused={specialtyFocused}
            locationFocused={locationFocused}
            setSpecialtyFocused={setSpecialtyFocused}
            setLocationFocused={setLocationFocused}
            setExpanded={setExpanded}
            setIsSearchMultiLine={setIsSearchMultiLine}
            isSearchMultiLine={isSearchMultiLine}
            setSearchResultsSingleViewIsShowing={setSearchResultsSingleViewIsShowing}
            searchResultsSingleViewIsShowing={searchResultsSingleViewIsShowing}
            showPlaceholder={showPlaceholder}
            setShowPlaceholder={setShowPlaceholder}
          />
        )}
        {!expanded && compactSearch && (
          <CompactSearchIconBackground onClick={() => onCompactToggle(!specialtyFocused)}>
            <CompactSearchIcon name="Search" />
          </CompactSearchIconBackground>
        )}

        {desktop && showExternalActions && (
          <DesktopNavMenu showLoginDropdown={showLoginDropdown} searchExperimentRunning={true} />
        )}

        {mobile && showExternalActions && (
          <div>
            {showAvatarCTA && (
              <NewHamburgerContainer isSearchMultiLine={isSearchMultiLine}>
                <StyledCTADot />
              </NewHamburgerContainer>
            )}
            {!specialtyFocused && !locationFocused && (
              <NewStyledHamburgerIcon isSearchMultiLine={isSearchMultiLine} onClick={openMenu} />
            )}
          </div>
        )}
      </NewHeaderContainer>

      {mobile && showExternalActions && (
        <SlideOut showing={showingMobileMenu} onClickOverlay={closeMenu}>
          <MobileMenu onClickLink={closeMenu} onClickLogout={closeMenu} />
        </SlideOut>
      )}
    </>
  );
};

export const renderNewQuery = (filters: FilterChoice): string => {
  const { topic, text } = filters;

  const { treatment, specialist, bulkBillingGp } = topic || {};

  if (text) return text;

  const topics = treatment ?? specialist;
  const suggestionType = bulkBillingGp ? 'Bulk Billing GP' : topics;

  return suggestionType ?? '';
};
